import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Phone, Siren, Shield, HeartPulse as FirstAid, Flame as Fire } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";

const ICONS = { police: Shield, ambulance: FirstAid, fire: Fire, disaster: Siren };

export default function Emergency() {
  const [contacts, setContacts] = useState([]);
  useEffect(() => { api.get("/emergency/contacts").then(r => setContacts(r.data)).catch(() => setContacts([{ key: "national", label: "National emergency", number: "112", color: "#dc2626", icon: "disaster" }])); }, []);

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-8">
      <div className="text-xs uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2"><Siren size={14} className="text-[#E05E36]" /> Rapid help desk</div>
      <h1 className="heading text-3xl md:text-5xl font-extrabold text-stone-900">Emergency helplines</h1>
      <p className="text-stone-600 mt-2 max-w-xl">Tap any number to dial instantly. Save this page for offline access.</p>

      <Link to="/sos/lift" data-testid="emergency-lift-sos-link"
            className="mt-6 flex items-center justify-between rounded-2xl bg-red-600 hover:bg-red-700 text-white px-5 py-4 font-black transition-colors">
        <span>Stuck in a lift? Get help now</span><span aria-hidden>→</span>
      </Link>
      <Link to="/ambulance" data-testid="emergency-ambulance-link"
            className="mt-3 flex items-center justify-between rounded-2xl bg-white border border-red-200 text-red-700 px-5 py-4 font-black hover:bg-red-50 transition-colors">
        <span>Private ambulance (verified providers)</span><span aria-hidden>→</span>
      </Link>

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {contacts.map((c, i) => {
          const Icon = ICONS[c.icon] || Siren;
          return (
            <motion.a
              key={c.key}
              href={`tel:${c.number}`}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              className="bg-white border border-stone-200 rounded-2xl p-5 flex items-center gap-4 hover:-translate-y-1 hover:shadow-md transition-transform group"
              data-testid={`help-${c.key}`}
            >
              <div className="w-14 h-14 rounded-2xl grid place-items-center text-white shrink-0" style={{ background: c.color }}>
                <Icon size={26} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">{c.label}</div>
                <div className="heading text-3xl font-extrabold text-stone-900" style={{ color: c.color }}>{c.number}</div>
              </div>
              <Phone className="text-stone-300 group-hover:text-stone-700" size={22} />
            </motion.a>
          );
        })}
      </div>

      <div className="mt-10 p-5 bg-stone-100 rounded-2xl text-sm text-stone-600 leading-relaxed">
        <b className="text-stone-900">Safety tips:</b> Stay calm. Share your exact location. If safe, use Find2Near's <a href="/alerts" className="text-[#E05E36] font-semibold">Alert feed</a> to warn neighbours.
      </div>
    </div>
  );
}
